import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import PersonIcon from '@mui/icons-material/PersonOutlineRounded'
import CheckIcon from '@mui/icons-material/Check'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LinkOffIcon from '@mui/icons-material/LinkOffRounded'
import { colors } from '../../const/colors'
import { CARD_SX, InfoField } from './UserDetailsUi'

const UserIdentityCard: FC<{
  fullName: string
  login: string
  firstName?: string
  lastName?: string
  workEmail?: string
  assigned: boolean
  onDetach: () => void
}> = ({
  fullName,
  login,
  firstName,
  lastName,
  workEmail,
  assigned,
  onDetach,
}) => {
  const { t } = useTranslation(['user_details', 'tenant', 'parking_pool'])

  return (
    <Box sx={CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <Box
          sx={{
            width: 58,
            height: 58,
            borderRadius: '50%',
            background: `linear-gradient(135deg,${colors.navyMid},${colors.navy})`,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 32 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: '19px',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
            }}
          >
            {fullName}
          </Typography>
          <Box
            sx={{
              mt: '9px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            {assigned ? (
              <>
                <Box
                  component="span"
                  data-e2e="service-badge"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '7px',
                    background: colors.green,
                    color: '#fff',
                    fontSize: '12.5px',
                    fontWeight: 800,
                    p: '7px 14px',
                    borderRadius: '20px',
                    boxShadow: '0 8px 16px -8px rgba(79,143,82,0.7)',
                  }}
                >
                  <CheckIcon sx={{ fontSize: 15 }} />
                  {t('parking_pool:assigned_service_badge')}
                </Box>
                <Box
                  component="span"
                  onClick={onDetach}
                  data-e2e="detach-user"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    p: '6px 13px',
                    borderRadius: '20px',
                    border: `1.5px solid ${colors.redBorder}`,
                    color: colors.red,
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'background .15s',
                    '&:hover': { background: colors.redBg },
                  }}
                >
                  <LinkOffIcon sx={{ fontSize: 15 }} />
                  {t('parking_pool:unassign_user_short')}
                </Box>
              </>
            ) : (
              <Box
                component="span"
                data-e2e="service-badge"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  background: colors.redBg,
                  color: colors.red,
                  fontSize: '12.5px',
                  fontWeight: 800,
                  p: '7px 14px',
                  borderRadius: '20px',
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 15 }} />
                {t('parking_pool:unassigned_service_badge')}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          mt: '18px',
          pt: '18px',
          borderTop: '1px solid #EFF3F6',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px 18px',
        }}
      >
        <InfoField
          label={t('form:full_name')}
          value={[firstName, lastName].filter(Boolean).join(' ')}
        />
        <InfoField label={t('form:login')} value={login} />
        <InfoField label={t('form:work_email')} value={workEmail} full />
      </Box>
    </Box>
  )
}

export default UserIdentityCard
